import {
  AudioDebugInfo,
  CharacterMode,
  EngineProfileConfig,
  FiringCharacteristic,
} from '../types';
import { CHARACTER_MODES } from './SoundProfile';

export class EngineSynth {
  private ctx: AudioContext;
  private destination: AudioNode;
  private profile: EngineProfileConfig;
  private character: CharacterMode = 'sport';

  // Master Synth Stages
  private masterGain: GainNode;
  private preSaturationGain: GainNode;
  private waveshaper: WaveShaperNode;

  // Discrete Acoustic Layers (Gains & Nodes)
  // Layer 1: Fundamental
  private fundamentalOsc: OscillatorNode | null = null;
  private fundamentalGain: GainNode;

  // Layer 2: Harmonics
  private harmonicOscs: OscillatorNode[] = [];
  private harmonicGains: GainNode[] = [];
  private harmonicsSubmix: GainNode;

  // Layer 3: Low-Frequency Body (Sub-bass / Camshaft half-order)
  private lowFreqBodyOsc: OscillatorNode | null = null;
  private lowFreqBodyFilter: BiquadFilterNode;
  private lowFreqBodyGain: GainNode;

  // Layer 4: Intake Airflow Roar
  private intakeNoiseSource: AudioBufferSourceNode | null = null;
  private intakeFilter: BiquadFilterNode;
  private intakeGain: GainNode;

  // Layer 5: Exhaust Resonators & Main Dynamic Lowpass
  private manifoldResonator: BiquadFilterNode;
  private exhaustThroatResonator: BiquadFilterNode;
  private mainFilter: BiquadFilterNode;

  // Layer 6: Mechanical Valvetrain Chatter
  private valvetrainNoiseSource: AudioBufferSourceNode | null = null;
  private valvetrainFilter: BiquadFilterNode;
  private valvetrainGain: GainNode;

  // Layer 7: High-Frequency Character / Metallic Rasp
  private highFreqRaspFilter: BiquadFilterNode;
  private highFreqRaspGain: GainNode;

  // Deceleration Overrun Gear Whine
  private overrunWhineOsc: OscillatorNode | null = null;
  private overrunWhineGain: GainNode;

  // Redline Limiter Flutter
  private limiterGain: GainNode;
  private isLimiterActive: boolean = false;
  private limiterInterval: number | null = null;

  // State Tracking
  private isRunning: boolean = false;
  private currentRpm: number = 750;
  private currentEffort: number = 0;
  private currentEngineBraking: number = 0;
  private lastBaseFrequencyHz: number = 50;
  private lastFilterCutoffHz: number = 380;
  private lastMasterSynthGain: number = 0.7;
  private currentRpmBand: 'idle' | 'low' | 'mid' | 'high' | 'redline' = 'idle';

  constructor(
    ctx: AudioContext,
    destination: AudioNode,
    profile: EngineProfileConfig,
    character: CharacterMode = 'sport'
  ) {
    this.ctx = ctx;
    this.destination = destination;
    this.profile = profile;
    this.character = character;

    const now = this.ctx.currentTime;

    // 1. Master Output Gain
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.setValueAtTime(0.001, now);

    // 2. Limiter Flutter Gain
    this.limiterGain = this.ctx.createGain();
    this.limiterGain.gain.setValueAtTime(1.0, now);

    // 3. Pre-Saturation Gain (Drives WaveShaper into non-linear combustion saturation)
    this.preSaturationGain = this.ctx.createGain();
    this.preSaturationGain.gain.setValueAtTime(1.0, now);

    // 4. WaveShaper Non-Linear Saturation Unit
    this.waveshaper = this.ctx.createWaveShaper();
    this.waveshaper.curve = this.createCombustionDistortionCurve(this.profile.distortionAmount) as Float32Array<ArrayBuffer>;
    this.waveshaper.oversample = '2x';

    // 5. Layer 1: Fundamental Gain
    this.fundamentalGain = this.ctx.createGain();
    this.fundamentalGain.gain.setValueAtTime(0.35, now);

    // 6. Layer 2: Harmonics Submix
    this.harmonicsSubmix = this.ctx.createGain();
    this.harmonicsSubmix.gain.setValueAtTime(0.35, now);

    // 7. Layer 3: Low-Frequency Body (Crankcase thump filter)
    this.lowFreqBodyFilter = this.ctx.createBiquadFilter();
    this.lowFreqBodyFilter.type = 'lowpass';
    this.lowFreqBodyFilter.frequency.setValueAtTime(140, now);
    this.lowFreqBodyFilter.Q.setValueAtTime(1.8, now);

    this.lowFreqBodyGain = this.ctx.createGain();
    this.lowFreqBodyGain.gain.setValueAtTime(this.profile.lowFreqBodyGain * 0.45, now);

    // 8. Layer 4: Intake Resonant Filter
    this.intakeFilter = this.ctx.createBiquadFilter();
    this.intakeFilter.type = 'bandpass';
    this.intakeFilter.frequency.setValueAtTime(450, now);
    this.intakeFilter.Q.setValueAtTime(2.2, now);

    this.intakeGain = this.ctx.createGain();
    this.intakeGain.gain.setValueAtTime(0.0, now);

    // 9. Layer 5: Exhaust Formant Cavity Resonators & Main Dynamic Lowpass
    this.manifoldResonator = this.ctx.createBiquadFilter();
    this.manifoldResonator.type = 'peaking';
    this.manifoldResonator.frequency.setValueAtTime(this.profile.filterCharacteristics.manifoldResonanceHz, now);
    this.manifoldResonator.Q.setValueAtTime(2.5, now);
    this.manifoldResonator.gain.setValueAtTime(4.5, now);

    this.exhaustThroatResonator = this.ctx.createBiquadFilter();
    this.exhaustThroatResonator.type = 'peaking';
    this.exhaustThroatResonator.frequency.setValueAtTime(this.profile.filterCharacteristics.exhaustThroatHz, now);
    this.exhaustThroatResonator.Q.setValueAtTime(2.2, now);
    this.exhaustThroatResonator.gain.setValueAtTime(3.8, now);

    this.mainFilter = this.ctx.createBiquadFilter();
    this.mainFilter.type = 'lowpass';
    this.mainFilter.frequency.setValueAtTime(this.profile.filterCharacteristics.idleCutoffHz, now);
    this.mainFilter.Q.setValueAtTime(this.profile.filterCharacteristics.resonanceQ, now);

    // 10. Layer 6: Mechanical Valvetrain Chatter
    this.valvetrainFilter = this.ctx.createBiquadFilter();
    this.valvetrainFilter.type = 'highpass';
    this.valvetrainFilter.frequency.setValueAtTime(2500, now);

    this.valvetrainGain = this.ctx.createGain();
    this.valvetrainGain.gain.setValueAtTime(this.profile.mechanicalNoise, now);

    // 11. Layer 7: High-Frequency Character / Metallic Rasp
    this.highFreqRaspFilter = this.ctx.createBiquadFilter();
    this.highFreqRaspFilter.type = 'peaking';
    this.highFreqRaspFilter.frequency.setValueAtTime(this.profile.filterCharacteristics.highFreqRaspHz, now);
    this.highFreqRaspFilter.Q.setValueAtTime(3.2, now);
    this.highFreqRaspFilter.gain.setValueAtTime(5.0, now);

    this.highFreqRaspGain = this.ctx.createGain();
    this.highFreqRaspGain.gain.setValueAtTime(0.05, now);

    // 12. Deceleration Overrun Whine
    this.overrunWhineGain = this.ctx.createGain();
    this.overrunWhineGain.gain.setValueAtTime(0.0, now);

    // AUDIO ROUTING TOPOLOGY:
    // (Fundamental + Harmonics) -> preSaturationGain -> waveshaper -> manifoldResonator -> exhaustThroatResonator -> mainFilter
    // (LowFreq Body) -----------> lowFreqBodyFilter -> manifoldResonator
    // (High-Freq Rasp) ---------> highFreqRaspFilter -> highFreqRaspGain -> mainFilter
    // (Intake Noise) -----------> intakeFilter ------> intakeGain ---------> mainFilter
    // (Valvetrain) -------------> valvetrainFilter --> valvetrainGain ------> mainFilter
    // (Overrun Whine) ----------> overrunWhineGain -------------------------> mainFilter
    // mainFilter -> limiterGain -> masterGain -> destination

    this.fundamentalGain.connect(this.preSaturationGain);
    this.harmonicsSubmix.connect(this.preSaturationGain);

    this.lowFreqBodyFilter.connect(this.lowFreqBodyGain);
    this.lowFreqBodyGain.connect(this.manifoldResonator);

    this.preSaturationGain.connect(this.waveshaper);
    this.waveshaper.connect(this.manifoldResonator);
    this.manifoldResonator.connect(this.exhaustThroatResonator);
    this.exhaustThroatResonator.connect(this.mainFilter);

    this.highFreqRaspFilter.connect(this.highFreqRaspGain);
    this.highFreqRaspGain.connect(this.mainFilter);

    this.intakeGain.connect(this.mainFilter);
    this.valvetrainGain.connect(this.mainFilter);
    this.overrunWhineGain.connect(this.mainFilter);

    this.mainFilter.connect(this.limiterGain);
    this.limiterGain.connect(this.masterGain);
    this.masterGain.connect(this.destination);
  }

  /**
   * Create an asymmetric non-linear saturation curve that simulates combustion chamber pressure
   */
  private createCombustionDistortionCurve(amount: number = 30): Float32Array {
    const samples = 2048;
    const curve = new Float32Array(samples);
    const k = amount;

    for (let i = 0; i < samples; ++i) {
      const x = (i * 2) / samples - 1;
      // Asymmetric quadratic component adds even harmonics (warmth & engine body)
      const shapedX = x + 0.12 * x * x;
      curve[i] = Math.tanh(k * 0.08 * shapedX) / Math.tanh(k * 0.08);
    }
    return curve;
  }

  /**
   * Create asymmetric periodic wave tailored to specific cylinder firing architecture
   */
  private createCombustionPulseWave(characteristic: FiringCharacteristic = this.profile.firingCharacteristics): PeriodicWave {
    const numHarmonics = characteristic === 'v12' ? 24 : 18;
    const real = new Float32Array(numHarmonics);
    const imag = new Float32Array(numHarmonics);

    real[0] = 0;
    imag[0] = 0;

    for (let n = 1; n < numHarmonics; n++) {
      real[n] = 0;
      switch (characteristic) {
        case 'v12':
          // Smooth, metallic, howling V12 combustion wave with extended high harmonics
          imag[n] = (1.0 / Math.pow(n, 0.92)) * Math.cos(n * 0.28);
          break;
        case 'v10':
          // Raspy, high-frequency odd-order resonance for iconic screaming V10
          imag[n] = (1.0 / Math.pow(n, 0.95)) * (n % 2 === 1 ? 1.15 : 0.85) * Math.cos(n * 0.24);
          break;
        case 'boxer':
          // Paired opposed pulses with punchy lower orders and scooped middle
          imag[n] = (1.0 / n) * (n === 2 || n === 4 ? 1.2 : 0.9) * Math.cos(n * 0.18);
          break;
        case 'crossplane':
          // Muscular uneven-pulse character with rich low harmonic weight
          imag[n] = (1.0 / Math.pow(n, 1.05)) * Math.cos(n * 0.15 + (n % 2 ? 0.2 : -0.1));
          break;
        case 'flatplane':
        case 'inline_even':
        default:
          imag[n] = (1.0 / n) * Math.cos(n * 0.2);
          break;
      }
    }

    return this.ctx.createPeriodicWave(real, imag, { disableNormalization: false });
  }

  /**
   * Initialize and start all layers
   */
  public start(): void {
    if (this.isRunning || this.ctx.state !== 'running') return;
    this.isRunning = true;

    const now = this.ctx.currentTime;
    this.buildOscillators(now);
    this.startNoiseSources(now);

    // Smooth un-mute
    this.masterGain.gain.setTargetAtTime(0.7, now, 0.1);
    this.updateParameters(this.profile.idleRpm, 0.0, 0);
  }

  /**
   * Rebuild or spawn the oscillator bank cleanly
   */
  private buildOscillators(now: number): void {
    // Synchronously stop and disconnect any existing oscillators
    this.cleanupOscillators();

    const pulseWave = this.createCombustionPulseWave(this.profile.firingCharacteristics);

    // 1. Layer 1: Fundamental Combustion Oscillator
    const fundOsc = this.ctx.createOscillator();
    fundOsc.setPeriodicWave(pulseWave);
    fundOsc.connect(this.fundamentalGain);
    fundOsc.start(now);
    this.fundamentalOsc = fundOsc;

    // 2. Layer 3: Low-Frequency Body Oscillator (Camshaft half-order)
    const bodyOsc = this.ctx.createOscillator();
    bodyOsc.type = 'triangle'; // Smooth round sub-bass thump
    bodyOsc.connect(this.lowFreqBodyFilter);
    bodyOsc.start(now);
    this.lowFreqBodyOsc = bodyOsc;

    // 3. Layer 2: Harmonics Bank
    this.harmonicOscs = [];
    this.harmonicGains = [];

    this.profile.harmonicRatios.forEach((ratio, i) => {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();

      // Alternate waveforms for richer timbre
      if (ratio % 1 === 0 && ratio % 2 === 0) {
        osc.type = 'sawtooth';
      } else if (ratio % 1 === 0.5) {
        osc.type = 'triangle';
      } else {
        osc.type = 'sawtooth';
      }

      // Apply detune if configured
      if (this.profile.harmonicDetunes && this.profile.harmonicDetunes[i]) {
        osc.detune.setValueAtTime(this.profile.harmonicDetunes[i], now);
      }

      const initialLevel = (this.profile.harmonicLevels[i] || 0.5) * 0.22;
      gain.gain.setValueAtTime(initialLevel, now);

      osc.connect(gain);
      gain.connect(this.harmonicsSubmix);

      osc.start(now);
      this.harmonicOscs.push(osc);
      this.harmonicGains.push(gain);
    });

    // 4. Deceleration Overrun Whine Oscillator
    const whine = this.ctx.createOscillator();
    whine.type = 'triangle';
    whine.connect(this.overrunWhineGain);
    whine.start(now);
    this.overrunWhineOsc = whine;
  }

  private startNoiseSources(now: number): void {
    this.cleanupNoiseSources();

    const bufferSize = this.ctx.sampleRate * 2;
    const noiseBuffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const output = noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      output[i] = Math.random() * 2 - 1;
    }

    // Layer 4: Intake Suction Noise
    const intakeSource = this.ctx.createBufferSource();
    intakeSource.buffer = noiseBuffer;
    intakeSource.loop = true;
    intakeSource.connect(this.intakeFilter);
    intakeSource.start(now);
    this.intakeNoiseSource = intakeSource;

    // Layer 6: Valvetrain Chatter Noise
    const valveSource = this.ctx.createBufferSource();
    valveSource.buffer = noiseBuffer;
    valveSource.loop = true;
    valveSource.connect(this.valvetrainFilter);
    valveSource.start(now);
    this.valvetrainNoiseSource = valveSource;
  }

  private cleanupOscillators(): void {
    if (this.fundamentalOsc) {
      try {
        this.fundamentalOsc.stop();
        this.fundamentalOsc.disconnect();
      } catch { /* ignore */ }
      this.fundamentalOsc = null;
    }

    if (this.lowFreqBodyOsc) {
      try {
        this.lowFreqBodyOsc.stop();
        this.lowFreqBodyOsc.disconnect();
      } catch { /* ignore */ }
      this.lowFreqBodyOsc = null;
    }

    this.harmonicOscs.forEach(osc => {
      try {
        osc.stop();
        osc.disconnect();
      } catch { /* ignore */ }
    });
    this.harmonicOscs = [];
    this.harmonicGains = [];

    if (this.overrunWhineOsc) {
      try {
        this.overrunWhineOsc.stop();
        this.overrunWhineOsc.disconnect();
      } catch { /* ignore */ }
      this.overrunWhineOsc = null;
    }
  }

  private cleanupNoiseSources(): void {
    if (this.intakeNoiseSource) {
      try {
        this.intakeNoiseSource.stop();
        this.intakeNoiseSource.disconnect();
      } catch { /* ignore */ }
      this.intakeNoiseSource = null;
    }

    if (this.valvetrainNoiseSource) {
      try {
        this.valvetrainNoiseSource.stop();
        this.valvetrainNoiseSource.disconnect();
      } catch { /* ignore */ }
      this.valvetrainNoiseSource = null;
    }
  }

  /**
   * Update all 7 engine sound layers smoothly in real time
   */
  public updateParameters(
    rpm: number,
    effort: number,
    engineBraking: number = 0
  ): void {
    if (!this.isRunning || this.ctx.state !== 'running') return;

    this.currentRpm = Math.max(100, rpm);
    this.currentEffort = Math.max(0, Math.min(1.0, effort));
    this.currentEngineBraking = Math.max(0, Math.min(1.0, engineBraking));

    const now = this.ctx.currentTime;
    const characterTuning = CHARACTER_MODES[this.character];
    const timeConstant = 0.035; // 35ms ramp: punchy and 100% click-free

    // 1. Identify RPM Band
    const idleRpm = this.profile.idleRpm;
    const redlineRpm = this.profile.redlineRpm;
    const rpmProgress = Math.max(0, Math.min(1.2, (this.currentRpm - idleRpm) / (redlineRpm - idleRpm)));

    if (this.currentRpm >= redlineRpm) {
      this.currentRpmBand = 'redline';
    } else if (rpmProgress >= 0.70) {
      this.currentRpmBand = 'high';
    } else if (rpmProgress >= 0.35) {
      this.currentRpmBand = 'mid';
    } else if (this.currentRpm >= idleRpm + 250) {
      this.currentRpmBand = 'low';
    } else {
      this.currentRpmBand = 'idle';
    }

    // 2. Calculate Base Combustion Firing Frequency (Hz)
    // 4-Stroke formula: f0 = (RPM / 60) * (cylinders / 2)
    const baseFiringFrequency = (this.currentRpm / 60) * (this.profile.cylinders / 2);
    this.lastBaseFrequencyHz = baseFiringFrequency;

    // Layer 1: Fundamental Pitch & Gain
    if (this.fundamentalOsc) {
      this.fundamentalOsc.frequency.setTargetAtTime(baseFiringFrequency, now, timeConstant);
      // Fundamental is strongest at idle/low, blends smoothly into overtone roar at high RPM
      const fundGain = (0.45 - rpmProgress * 0.15) * characterTuning.volumeMultiplier;
      this.fundamentalGain.gain.setTargetAtTime(fundGain, now, timeConstant);
    }

    // Layer 3: Low-Frequency Body (Camshaft half-order: 0.5 * f0)
    if (this.lowFreqBodyOsc) {
      const camFreq = Math.max(15, baseFiringFrequency * 0.5);
      this.lowFreqBodyOsc.frequency.setTargetAtTime(camFreq, now, timeConstant);
      // Heavy body at idle, receding as engine revs higher
      const bodyLevel = (1.0 - Math.min(0.85, rpmProgress * 0.7)) * this.profile.lowFreqBodyGain * 0.4;
      this.lowFreqBodyGain.gain.setTargetAtTime(bodyLevel, now, timeConstant);
      this.lowFreqBodyFilter.frequency.setTargetAtTime(110 + rpmProgress * 60, now, timeConstant);
    }

    // Layer 2: Harmonics Bank
    this.profile.harmonicRatios.forEach((ratio, i) => {
      const osc = this.harmonicOscs[i];
      const gainNode = this.harmonicGains[i];
      if (!osc || !gainNode) return;

      const targetFreq = Math.max(12, baseFiringFrequency * ratio);
      osc.frequency.setTargetAtTime(targetFreq, now, timeConstant);

      // Upper harmonics grow aggressively with RPM and throttle effort
      let scale = this.profile.harmonicLevels[i] || 0.5;
      if (ratio >= 2.0) {
        scale += rpmProgress * 0.6 + this.currentEffort * 0.3;
      }

      const targetGain = scale * 0.24 * characterTuning.volumeMultiplier;
      gainNode.gain.setTargetAtTime(targetGain, now, timeConstant);
    });

    // Layer 5: Pre-Saturation Overdrive & Cavity Resonators
    // Drives into Tanh WaveShaper: clean at idle/cruise, gritty combustion roar under load
    const saturationDrive = (0.9 + this.currentEffort * 1.8 * this.profile.exhaustIntensity) * characterTuning.exhaustMultiplier;
    this.preSaturationGain.gain.setTargetAtTime(saturationDrive, now, timeConstant);

    // Dynamic Lowpass Filter: Sweeps from idle cutoff up to redline cutoff
    const filterBase = this.profile.filterCharacteristics.idleCutoffHz;
    const filterMax = this.profile.filterCharacteristics.redlineCutoffHz * characterTuning.filterCutoffMultiplier;
    const dynamicCutoff = filterBase + (filterMax - filterBase) * (rpmProgress * 0.6 + this.currentEffort * 0.4);
    this.lastFilterCutoffHz = dynamicCutoff;
    this.mainFilter.frequency.setTargetAtTime(Math.min(18000, dynamicCutoff), now, timeConstant);

    // Layer 4: Resonant Intake Airflow Suction Roar
    const intakeTargetGain =
      this.currentEffort * this.profile.intakeIntensity * characterTuning.intakeMultiplier * (0.35 + 0.65 * rpmProgress);
    this.intakeGain.gain.setTargetAtTime(intakeTargetGain, now, timeConstant);
    // Center frequency climbs with airflow velocity
    const intakeFreq = 340 + rpmProgress * 480 + this.currentEffort * 280;
    this.intakeFilter.frequency.setTargetAtTime(intakeFreq, now, timeConstant);

    // Layer 6: Mechanical Valvetrain Chatter
    const valveGain = (this.profile.mechanicalNoise + rpmProgress * 0.035) * characterTuning.volumeMultiplier;
    this.valvetrainGain.gain.setTargetAtTime(valveGain, now, timeConstant);

    // Layer 7: High-Frequency Character & Metallic Rasp (active at high RPM)
    const raspGain = Math.max(0, (rpmProgress - 0.45) / 0.55) * this.profile.highFreqCharacterGain * 0.35;
    this.highFreqRaspGain.gain.setTargetAtTime(raspGain, now, timeConstant);

    // Deceleration Overrun Whine (straight-cut gear / flywheel whine on throttle lift)
    if (this.overrunWhineOsc) {
      if (this.currentEngineBraking > 0.05 && this.currentRpm > idleRpm + 350) {
        const whineFreq = baseFiringFrequency * 2.85;
        this.overrunWhineOsc.frequency.setTargetAtTime(whineFreq, now, timeConstant);
        const whineLevel = this.currentEngineBraking * 0.11 * this.profile.engineBrakingResponse * (this.currentRpm / redlineRpm);
        this.overrunWhineGain.gain.setTargetAtTime(whineLevel, now, timeConstant);
      } else {
        this.overrunWhineGain.gain.setTargetAtTime(0.0, now, timeConstant);
      }
    }

    // Master Synth Gain: Cruising vs Acceleration vs Overrun Decel
    let targetVol = 0.52 + this.currentEffort * 0.48;
    if (this.currentEngineBraking > 0.05) {
      // Hollow overrun tone with lower volume but elevated pitch
      targetVol = 0.40 + this.currentEngineBraking * 0.15;
    }
    targetVol *= characterTuning.volumeMultiplier;
    this.lastMasterSynthGain = targetVol;
    this.masterGain.gain.setTargetAtTime(targetVol, now, timeConstant);

    // Redline Limiter Handling
    if (this.currentRpm >= redlineRpm) {
      this.activateLimiter(characterTuning.limiterStyle);
    } else {
      this.deactivateLimiter();
    }
  }

  /**
   * Switch engine profile cleanly with ZERO audio node destruction race conditions!
   * Completely resolves the engine switching bug.
   */
  public setProfile(profile: EngineProfileConfig): void {
    this.profile = profile;

    const now = this.ctx.currentTime;

    // 1. Update filter & waveshaper parameters immediately
    this.mainFilter.Q.setValueAtTime(profile.filterCharacteristics.resonanceQ, now);
    this.manifoldResonator.frequency.setValueAtTime(profile.filterCharacteristics.manifoldResonanceHz, now);
    this.exhaustThroatResonator.frequency.setValueAtTime(profile.filterCharacteristics.exhaustThroatHz, now);
    this.highFreqRaspFilter.frequency.setValueAtTime(profile.filterCharacteristics.highFreqRaspHz, now);
    this.waveshaper.curve = this.createCombustionDistortionCurve(profile.distortionAmount) as Float32Array<ArrayBuffer>;

    // 2. If running, rebuild oscillators synchronously without any setTimeout race condition!
    if (this.isRunning && this.ctx.state === 'running') {
      this.buildOscillators(now);
      this.updateParameters(this.currentRpm, this.currentEffort, this.currentEngineBraking);
    }
  }

  public setCharacter(character: CharacterMode): void {
    this.character = character;
  }

  public getCurrentRpm(): number {
    return this.currentRpm;
  }

  public getCurrentEffort(): number {
    return this.currentEffort;
  }

  public getCurrentEngineBraking(): number {
    return this.currentEngineBraking;
  }

  /**
   * Real-time audio parameter debug data for all 7 layers
   */
  public getDebugInfo(): AudioDebugInfo {
    return {
      baseFrequencyHz: Math.round(this.lastBaseFrequencyHz * 10) / 10,
      fundamentalGain: Math.round(this.fundamentalGain.gain.value * 100) / 100,
      harmonicsGain: Math.round(this.harmonicsSubmix.gain.value * 100) / 100,
      lowFreqBodyGain: Math.round(this.lowFreqBodyGain.gain.value * 100) / 100,
      intakeGain: Math.round(this.intakeGain.gain.value * 100) / 100,
      exhaustGain: Math.round(this.preSaturationGain.gain.value * 100) / 100,
      mechanicalNoiseGain: Math.round(this.valvetrainGain.gain.value * 100) / 100,
      highFreqRaspGain: Math.round(this.highFreqRaspGain.gain.value * 100) / 100,
      engineBrakingWhineGain: Math.round(this.overrunWhineGain.gain.value * 100) / 100,
      filterCutoffHz: Math.round(this.lastFilterCutoffHz),
      filterQ: Math.round(this.profile.filterCharacteristics.resonanceQ * 10) / 10,
      saturationDrive: Math.round((0.9 + this.currentEffort * 1.8) * 100) / 100,
      masterSynthGain: Math.round(this.lastMasterSynthGain * 100) / 100,
      currentRpmBand: this.currentRpmBand,
      audioContextState: this.ctx.state,
      sampleRate: this.ctx.sampleRate,
    };
  }

  private activateLimiter(style: 'soft' | 'flutter' | 'aggressive'): void {
    if (this.isLimiterActive) return;
    this.isLimiterActive = true;

    if (style === 'soft') {
      this.limiterGain.gain.setTargetAtTime(0.75, this.ctx.currentTime, 0.02);
      return;
    }

    let flutterState = false;
    this.limiterInterval = window.setInterval(() => {
      flutterState = !flutterState;
      const target = flutterState ? 1.0 : (style === 'aggressive' ? 0.05 : 0.25);
      if (this.ctx.state === 'running') {
        this.limiterGain.gain.setValueAtTime(target, this.ctx.currentTime);
      }
    }, 45);
  }

  private deactivateLimiter(): void {
    if (!this.isLimiterActive) return;
    this.isLimiterActive = false;
    if (this.limiterInterval !== null) {
      clearInterval(this.limiterInterval);
      this.limiterInterval = null;
    }
    if (this.ctx.state === 'running') {
      this.limiterGain.gain.setTargetAtTime(1.0, this.ctx.currentTime, 0.04);
    }
  }

  public stop(): void {
    if (!this.isRunning) return;
    this.isRunning = false;
    this.deactivateLimiter();

    const now = this.ctx.currentTime;
    this.masterGain.gain.setTargetAtTime(0.001, now, 0.08);

    this.cleanupNoiseSources();
    this.cleanupOscillators();
  }
}
